import type { IActionsPreset, IPreset } from './types';
import {
  getOrCreateRootFolder,
  createSubfolder,
  registerFileInDrive,
  updateFolderAttributes,
  uploadBlobToCdn,
  fetchBlobFromUrl,
  searchSubfolders,
  deleteFile,
  HIDDEN_ATTRIBUTES,
  PRESET_CONTENT_LINK_ATTR,
} from './driveClient';
import { extractAssetUrls, rewriteAssetUrl, isPicsartUrl, isBlobOrDataUrl, getExtFromMime, getResourceType, getResourceTypeFromUrl } from './assetUtils';

export async function savePresetToDrive(preset: IActionsPreset): Promise<IActionsPreset> {
  const rootFolderId = await getOrCreateRootFolder();
  const subFolderId = await createSubfolder(preset.id, rootFolderId);

  try {
    const presetCopy: IActionsPreset = JSON.parse(JSON.stringify(preset));
    const assets = extractAssetUrls(presetCopy);

    await Promise.all(
      assets.map(async asset => {
        try {
          if (isPicsartUrl(asset.sourceUrl) && !isBlobOrDataUrl(asset.sourceUrl)) {
            await registerFileInDrive(asset.driveName, asset.sourceUrl, subFolderId, getResourceTypeFromUrl(asset.sourceUrl));
          } else {
            const blob = await fetchBlobFromUrl(asset.sourceUrl);
            const ext = getExtFromMime(blob.type);
            const cdnUrl = await uploadBlobToCdn(blob);
            await registerFileInDrive(`${asset.driveName}${ext}`, cdnUrl, subFolderId, getResourceType(blob.type));
            rewriteAssetUrl(presetCopy, asset, cdnUrl);
          }
        } catch (error) {
          console.warn(`Failed to persist asset ${asset.driveName}:`, error);
        }
      }),
    );

    const jsonBlob = new Blob([JSON.stringify(presetCopy)], { type: 'application/json' });
    const jsonCdnUrl = await uploadBlobToCdn(jsonBlob);
    await registerFileInDrive('preset.json', jsonCdnUrl, subFolderId);

    await updateFolderAttributes(subFolderId, {
      ...HIDDEN_ATTRIBUTES,
      [PRESET_CONTENT_LINK_ATTR]: jsonCdnUrl,
    });

    return { ...presetCopy, driveFolderId: subFolderId };
  } catch (error) {
    // Clean up the orphaned subfolder if save fails after folder creation
    deleteFile(subFolderId).catch(() => {});
    throw error;
  }
}

export async function loadPresetsFromDrive(): Promise<IActionsPreset[]> {
  const rootFolderId = await getOrCreateRootFolder();
  const folders = await searchSubfolders(rootFolderId);

  const presets = await Promise.all(
    folders.map(async folder => {
      try {
        const cdnUrl = folder.attributes?.[PRESET_CONTENT_LINK_ATTR];
        if (!cdnUrl) return null;

        const contentRes = await fetch(cdnUrl);
        if (!contentRes.ok) return null;
        const raw = (await contentRes.json()) as Partial<IActionsPreset> & { type?: IPreset['type'] };
        if (!raw.id || !raw.steps || !raw.preview) return null;
        // Drive only stores user-saved presets, which are always 'actions'.
        // Legacy presets predate the discriminator, so default type here.
        const preset: IActionsPreset = { ...(raw as IActionsPreset), type: 'actions', driveFolderId: folder.id };
        return preset;
      } catch {
        return null;
      }
    }),
  );

  return presets.filter((p): p is IActionsPreset => p !== null);
}

export async function deletePresetFromDrive(driveFolderId: string): Promise<void> {
  await deleteFile(driveFolderId);
}

export async function updatePresetOnDrive(preset: IActionsPreset): Promise<IActionsPreset> {
  if (preset.driveFolderId) {
    await deletePresetFromDrive(preset.driveFolderId);
  }
  return savePresetToDrive(preset);
}
